import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StartupSweepGate from '../components/StartupSweep/StartupSweepGate';
import { useNavigate } from 'react-router-dom';

// @mui
import {
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// icons
import WorkIcon from '@mui/icons-material/Work';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import BuildIcon from '@mui/icons-material/Build';

// feature slices
import { getJobs } from '../feature/jobSlice';
import { getTasks } from '../feature/tasksSlice';
import { getInventory } from '../feature/inventorySlice';

// utils
import { fDate } from '../utils/formatTime';

import SmartTable from "../components/SmartTable/SmartTable";
// ----------------------------------------------------------------------

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 0.8 }}>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.2, mt: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette[color]?.light || theme.palette.primary.light, 0.30)}, ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.18)})`,
              color: (theme) => theme.palette[color]?.main || theme.palette.primary.main,
              flexShrink: 0,
              boxShadow: (theme) =>
                `0 2px 8px ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.20)}`,
            }}
          >
            <Icon fontSize="medium" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function JobStatusChip({ status }) {
  const map = {
    completed: { label: 'Completed', color: 'success' },
    in_process: { label: 'In Progress', color: 'warning' },
    pending: { label: 'Pending', color: 'default' },
    priority: { label: 'Priority', color: 'error' },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// eslint-disable-next-line no-unused-vars
function TaskStatusChip({ status }) {
  const map = {
    approved: { label: 'Approved', color: 'success' },
    in_process: { label: 'In Progress', color: 'warning' },
    pending: { label: 'Pending', color: 'default' },
    rejected: { label: 'Rejected', color: 'error' },
  };
  const cfg = map[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ----------------------------------------------------------------------

export default function DashboardAppPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { jobs, isJobLoading } = useSelector((state) => state.jobSlice);
  const { tasks, isTaskLoading } = useSelector((state) => state.tasksSlice);
  const { inventory, isInventoryLoading } = useSelector((state) => state.inventorySlice);
  const { decodedToken } = useSelector((state) => state.userSlice.loginUser);

  useEffect(() => {
    dispatch(getJobs());
    dispatch(getTasks());
    dispatch(getInventory());
  }, [dispatch]);

  // ââ Derived KPIs ââââââââââââââââââââââââââââââââââââââââââââââââââââââ
  const activeJobs = jobs.filter((j) => j.status === 'in_process' || j.status === 'priority');
  const completedJobs = jobs.filter((j) => j.status === 'completed');
  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  const inProgressTasks = tasks.filter((t) => t.status === 'in_process');
  const approvedTasks = tasks.filter((t) => t.status === 'approved');
  const rejectedTasks = tasks.filter((t) => t.status === 'rejected' || t.status === 'rejected_qc');

  const taskCompletionRate =
    tasks.length > 0 ? Math.round((approvedTasks.length / tasks.length) * 100) : 0;

  // Recent active jobs (up to 8) \u2014 priority jobs first
  const recentJobs = [...activeJobs]
    .sort((a, b) => (a.status === 'priority' ? -1 : 1) - (b.status === 'priority' ? -1 : 1))
    .slice(0, 8);

  return (
    <StartupSweepGate routeKey="dashboard">
      <Helmet>
        <title>Dashboard | SFM</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* ââ Header ââââââââââââââââââââââââââââââââââââââââââââââââââââ */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Good {getTimeOfDay()}, {decodedToken?.firstName || 'there'} {decodedToken?.lastName || ''}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Here's what's happening on the floor today.
          </Typography>
        </Box>

        {/* ââ KPI Cards âââââââââââââââââââââââââââââââââââââââââââââââââ */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Jobs"
              value={isJobLoading ? '\u2014' : activeJobs.length}
              subtitle={`${completedJobs.length} completed · ${jobs.length} total`}
              icon={WorkIcon}
              color="warning"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Tasks"
              value={isTaskLoading ? '\u2014' : pendingTasks.length}
              subtitle={`${inProgressTasks.length} in progress`}
              icon={PendingIcon}
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Approved Tasks"
              value={isTaskLoading ? '\u2014' : approvedTasks.length}
              subtitle={`${taskCompletionRate}% approval rate`}
              icon={CheckCircleIcon}
              color="success"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Inventory Items"
              value={isInventoryLoading ? '\u2014' : inventory.length}
              subtitle="Total tracked items"
              icon={InventoryIcon}
              color="info"
            />
          </Grid>
        </Grid>

        {/* ââ Task Completion Bar ââââââââââââââââââââââââââââââââââââââââ */}
        {tasks.length > 0 && (
          <Card sx={{ mb: 4, p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Task Status Overview
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', width: 120 }}>
                Approval Rate
              </Typography>
              <LinearProgress
                variant="determinate"
                value={taskCompletionRate}
                color="success"
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 40 }}>
                {taskCompletionRate}%
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="caption">Approved ({approvedTasks.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="caption">In Progress ({inProgressTasks.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.400' }} />
                <Typography variant="caption">Pending ({pendingTasks.length})</Typography>
              </Box>
              {rejectedTasks.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
                  <Typography variant="caption">Rejected ({rejectedTasks.length})</Typography>
                </Box>
              )}
            </Stack>
          </Card>
        )}

        {/* ââ Active Jobs Table ââââââââââââââââââââââââââââââââââââââââââ */}
        <Card>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Active Jobs
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
              onClick={() => navigate('/jobs')}
            >
              View all →
            </Typography>
          </Box>

          <Divider />

          {isJobLoading ? (
            <LinearProgress />
          ) : recentJobs.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <BuildIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No active jobs at the moment.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <SmartTable tableId="dashboard-jobs" lockedColumns={["Job Name", "Status"]} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Job Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>End Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentJobs.map((job) => {
                    const progress =
                      job.totalTasks > 0
                        ? Math.round((job.completedTasks / job.totalTasks) * 100)
                        : 0;
                    return (
                      <TableRow
                        key={job.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/detail-Job/${job.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {job.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <JobStatusChip status={job.status} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              color="success"
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {job.completedTasks ?? 0}/{job.totalTasks ?? 0}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {job.startDate ? fDate(job.startDate) : '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {job.endDate ? fDate(job.endDate) : '\u2014'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SmartTable>
            </TableContainer>
          )}
        </Card>
      </Container>
    </StartupSweepGate>
  );
}

// ----------------------------------------------------------------------

function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}
